import { Box, Stack, StackProps, styled, Typography } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import { Note } from '@phosphor-icons/react';
import { t } from 'i18next';
import { CSSProperties } from 'react';

import { isEmptyArray, parseString } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { Color, Level } from 'theme/enums';

import { FlexBox } from './FlexBox';
import Tooltip from './Tooltip';

interface IProps extends StackProps {
  amount: number;
  decimals?: number;
  fixedDecimals?: boolean;
  label?: string;
  labelColor?: Color;
  labelStyle?: SxProps;
  color?: Color;
  style?: CSSProperties;
  notes?: string[];
  level?: Level;
}

const Wrapper = styled(Stack)({
  justifyContent: 'inherit',
  alignItems: 'flex-end',
});

const getNotesContent = (notes: string[]) => {
  return (
    <Stack>
      <Typography color={Color.NEUTRAL} level={Level.BODY_SM}>{`${t(
        'labelCostNotes'
      )}:`}</Typography>

      {notes.map((note, index) => (
        <Box key={index}>{parseString(note)}</Box>
      ))}
    </Stack>
  );
};

const getPriceElementContent = ({
  amount,
  decimals,
  fixedDecimals,
  color,
  notes,
  level,
  onClick,
}) => {
  const priceContent = (
    <Typography
      level={level}
      color={color}
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {PricingHelper.formatPricing({
        amount,
        decimals,
        fixedDecimals,
      })}
    </Typography>
  );

  if (isEmptyArray(notes)) {
    return priceContent;
  }

  return (
    <FlexBox>
      {priceContent}
      <Tooltip title={getNotesContent(notes)}>
        <Note />
      </Tooltip>
    </FlexBox>
  );
};

const Price = ({
  amount,
  decimals,
  fixedDecimals = false,
  label,
  labelColor,
  labelStyle,
  color,
  style,
  notes = [],
  level = Level.BODY_SM,
  onClick = null,
  ...rest
}: IProps) => {
  return (
    <Wrapper sx={style} spacing={0} {...rest}>
      {label && (
        <Typography
          level={Level.BODY_XS}
          color={labelColor || color || Color.NEUTRAL}
          sx={labelStyle}
        >
          {label}
        </Typography>
      )}
      {getPriceElementContent({ amount, decimals, fixedDecimals, color, notes, level, onClick })}
    </Wrapper>
  );
};

export default Price;
